import { Checkbox, Grid, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PositionControl } from './position-control/position-control';
import { FormattingCell } from './formatting-cell/formatting-cell';
import { AppearanceCell } from './appearance-cell/appearance-cell';
import { useFormikContext } from 'formik';
import { useCallback } from 'react';
import { UpdateConfigDialogFormValues } from '../../dto';
import type { ColumnMappingProps } from './dto';

export const ColumnMapping = ({ currentLanguage, languagesUsed }: ColumnMappingProps) => {
    const { isSubmitting, values, getFieldProps, setFieldValue } = useFormikContext<UpdateConfigDialogFormValues>();

    const moveColumn = useCallback(
        (previousIndex: number, newIndex: number) => {
            setFieldValue(
                'config.columns',
                values.config.columns.map((column, index, columns) => {
                    if (index === previousIndex) {
                        return columns[newIndex];
                    }
                    if (index === newIndex) {
                        return columns[previousIndex];
                    }

                    return column;
                }),
            );
        },
        [values.config.columns, setFieldValue],
    );

    return (
        <Table style={isSubmitting ? { opacity: 0.7, pointerEvents: 'none', cursor: 'not-allowed' } : undefined}>
            <TableHead>
                <TableRow>
                    <TableCell>
                        <FormattedMessage id="inside-fifa.generic-tables.mapping-table-header.order" />
                    </TableCell>
                    <TableCell>
                        <FormattedMessage id="inside-fifa.generic-tables.mapping-table-header.source-column" />
                    </TableCell>
                    <TableCell>
                        <FormattedMessage
                            id="inside-fifa.generic-tables.mapping-table-header.localised-column"
                            values={{ langCode: currentLanguage.toUpperCase() }}
                        />
                    </TableCell>
                    <TableCell>
                        <FormattedMessage id="inside-fifa.generic-tables.mapping-table-header.formatting" />
                    </TableCell>
                    <TableCell>
                        <FormattedMessage id="inside-fifa.generic-tables.mapping-table-header.appearance" />
                    </TableCell>
                    <TableCell>
                        <FormattedMessage id="inside-fifa.generic-tables.mapping-table-header.sorting" />
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {values.config.columns.map((column, index) => {
                    return (
                        <TableRow key={column.key || index}>
                            <TableCell>
                                <Grid container alignItems="center" columnGap={1}>
                                    <span style={{ minWidth: 32 }}>{`#${index + 1}`}</span>
                                    <PositionControl
                                        index={index}
                                        maxIndex={values.config.columns.length - 1}
                                        onIndexChange={moveColumn}
                                    />
                                </Grid>
                            </TableCell>
                            <TableCell>{column.key}</TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    disabled={isSubmitting}
                                    {...getFieldProps(
                                        values.config.columns[index].columnTitle
                                            ? `config.columns[${index}].columnTitle[${currentLanguage}]`
                                            : `config.columns[${index}].title`,
                                    )}
                                />
                            </TableCell>
                            <TableCell>
                                <FormattingCell
                                    column={column}
                                    languages={languagesUsed}
                                    onChange={(newColumn) => setFieldValue(`config.columns[${index}]`, newColumn)}
                                />
                            </TableCell>
                            <TableCell>
                                <AppearanceCell
                                    column={column}
                                    onChange={(newColumn) => {
                                        setFieldValue(`config.columns[${index}]`, newColumn);
                                    }}
                                />
                            </TableCell>
                            <TableCell>
                                <Checkbox
                                    checked={column.isSortingEnabled}
                                    onChange={(_event, checked) =>
                                        setFieldValue(`config.columns[${index}].isSortingEnabled`, checked)
                                    }
                                />
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
};
